import { useEffect } from "react";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import { useQueryClient } from "react-query";
import { useStoreState } from "atoms/storeAtom";

import { BoldText } from "components/BoldText/BoldText";
import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { Option } from "components/Select/Select";

import styles from "./styles.module.scss";
import { NewShopFooter, NewShopBody } from "./components";

export type FromShopValues = {
  name: string;
  storesid: string;
  selectedPricelistItem: Option | null;
  selectedArticleListItem: Option | null;
  usercalctype: string;
  dayopentimeafter: string;
  nightopentimeafter: string;
  useroperationforpercent: boolean;
  usergetpercentbyplan: boolean;
};

const Header = ({ name }: { name: string }): JSX.Element => (
  <BoldText className={styles.groupTitle}>
    {!!name ? name : "Новый магазин"}
  </BoldText>
);

export const USER_CALC_TYPE_CONSTANTS = [
  { name: "Сотрудник получает % со своих продаж", value: "0" },
  { name: "Сотрудник получает % со всех продаж", value: "1" },
];

export const NewShop = (): JSX.Element => {
  const { storeState } = useStoreState();
  const queryClient = useQueryClient();

  const isEdit = Boolean(storeState.activeItem);

  // ВНИМАНИЕ
  // В ТЕКУЩЕЙ ВЕРСИИ АКТУАЛЬНЫ ТОЛЬКО ПОЛЯ НАЗВАНИЕ, ПРАЙСЛИСТ И СТАТЬЯ ЗАТРАТ
  // ОСТАЛЬНЫЕ ПОЛЯ БЫЛИ НАПОЛОВИНУ РЕАЛИЗОВАНЫ И ПОКА БУДУТ СКРЫТЫ В ЭТОЙ ВЕРСИИ

  const { handleSubmit, control, reset, watch } = useForm<FromShopValues>({
    defaultValues: {
      usercalctype: "1",
      selectedPricelistItem: null,
      selectedArticleListItem: null,
      name: "",
      dayopentimeafter: "00:00:00",
      nightopentimeafter: "00:00:00",
      useroperationforpercent: false,
      usergetpercentbyplan: false,
    },
  });

  useEffect(() => {
    if (storeState.activeItem) {
      const priceList: any[] = Array.from(
        queryClient.getQueryData(["get-pricelist"]) || []
      );
      const articlesList: any[] = Array.from(
        queryClient.getQueryData(["get-articles-list"]) || []
      );

      const selectedPricelistItem = priceList.find(
        (item: any) => item.pricelistid === storeState.activeItem.pricelistid
      );

      const selectedArticle = articlesList.find(
        (item: any) => item.articleid === storeState.activeItem.articleid
      );

      const selectedPricelistOption = selectedPricelistItem
        ? {
            label: selectedPricelistItem.name,
            value: selectedPricelistItem.pricelistid,
          }
        : null;

      const selectedArticleOption = selectedArticle
        ? {
            label: selectedArticle.name,
            value: selectedArticle.articleid,
          }
        : null;

      reset({
        storesid: storeState.activeItem.storesid,
        usercalctype: storeState.activeItem.usercalctype + "",
        selectedPricelistItem: selectedPricelistOption,
        selectedArticleListItem: selectedArticleOption,
        name: storeState.activeItem.name,
        dayopentimeafter: storeState.activeItem.dayopentimeafter,
        nightopentimeafter: storeState.activeItem.nightopentimeafter,
        useroperationforpercent: storeState.activeItem.useroperationforpercent,
        usergetpercentbyplan: storeState.activeItem.usergetpercentbyplan,
      });
    } else {
      reset({
        storesid: "null",
        usercalctype: "1",
        selectedPricelistItem: null,
        selectedArticleListItem: null,
        name: "",
        dayopentimeafter: "00:00:00",
        nightopentimeafter: "00:00:00",
        useroperationforpercent: false,
        usergetpercentbyplan: false,
      });
    }
  }, [storeState]);

  return (
    <BaseLayout
      className={classNames("ml-2 mt-3 mb-3", styles.container)}
      header={<Header name={watch("name")} />}
      footer={<NewShopFooter handleSubmit={handleSubmit} />}
    >
      <NewShopBody control={control} isEdit={isEdit} />
    </BaseLayout>
  );
};
